/**
 * Cookie utility functions for secure device token storage
 * Provides fallback storage when localStorage is cleared by browser policies
 */

/**
 * Set a cookie with specified name, value and days to expire
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Days until expiration (default: 14 days for MFA grace period)
 */
export const setCookie = (name, value, days = 14) => {
    try {
        let expires = "";
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            expires = "; expires=" + date.toUTCString();
        }
        
        // Set cookie with security flags
        // SameSite=Strict prevents CSRF attacks
        // Secure flag should be added in production (requires HTTPS)
        const isProduction = process.env.NODE_ENV === 'production';
        const secureFlag = isProduction ? '; Secure' : '';
        
        document.cookie = name + "=" + (value || "") + expires + "; path=/" + secureFlag + "; SameSite=Strict";
        
        return true;
    } catch (e) {
        console.error('[cookieUtil] Error setting cookie:', e);
        return false;
    }
}

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
    try {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) {
                return c.substring(nameEQ.length, c.length);
            }
        }
        return null;
    } catch (e) {
        console.error('[cookieUtil] Error reading cookie:', e);
        return null;
    }
}

/**
 * Delete a cookie by name
 * @param {string} name - Cookie name
 */
export const deleteCookie = (name) => {
    try {
        document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        return true;
    } catch (e) {
        console.error('[cookieUtil] Error deleting cookie:', e);
        return false;
    }
}

/**
 * Check if cookies are enabled in the browser
 * @returns {boolean} True if cookies are enabled
 */
export const areCookiesEnabled = () => {
    try {
        // Test if we can set a cookie
        document.cookie = 'cookietest=1';
        const cookiesEnabled = document.cookie.indexOf('cookietest=') !== -1;
        // Delete test cookie
        document.cookie = 'cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT';
        return cookiesEnabled;
    } catch (e) {
        return false;
    }
}
